import { createSelector } from 'redux-orm'
import { orm } from './orm'
import { isSameWeek } from '@fedsim/helpers'

// sort
export const sortByPoints = (a, b) => b?.points - a?.points
export const sortByOrder = (a, b) => b?.order - a?.order
export const sortByWins = (a, b) => b?.wins > a?.wins
export const sortByRepeatIndex = (a, b) => a?.dayIndex - b?.dayIndex
export function sortByDate(a, b) {
  return new Date(b.date) - new Date(a.date)
}

// direct orm pick
export const getDrafts = createSelector(orm.Draft)
export const getFactions = createSelector(orm.Faction)
export const getGame = createSelector(orm.Game)
export const getProductions = createSelector(orm.Production)
export const getReigns = createSelector(orm.Reign)
export const getSegments = createSelector(orm.Segment)
export const getShows = createSelector(orm.Show)
export const getBrands = createSelector(orm.Brand)
export const getWrestlers = createSelector(orm.Wrestler)
export const getChampionships = createSelector(orm.Championship)
export const getFeuds = createSelector(orm.Feud)
export const getArenas = createSelector(orm.Arena)
export const getInjuries = createSelector(orm.Injury)
export const getLedgers = createSelector(orm.Ledger)
export const getAppearances = createSelector(orm.Appearance)
export const getRumbles = createSelector(orm.Rumble)
export const getContracts = createSelector(orm.Contract)
export const getNotifications = createSelector(orm.Notification)
export const getTournaments = createSelector(orm.Tournament)
export const getMails = createSelector(orm.Mail)
// getModeledMailse

export const getDirectors = brandIds => createSelector(orm, session => {
  const result = session.Brand.all()
    .toModelArray()
    .map(joins => {
      return {
        ...joins._fields,
        director: joins?.director?._fields
      }
    })
    .filter(item => brandIds?.includes(item.id))

  return result
})

// singular
export const getProduction = id =>
  createSelector(orm, session => {
    if (!id) return

    const item = session.Production.withId(id)

    return modelProduction(item, session)
  })

// modeled collection
export const getModeledTournaments = createSelector(orm, session => {
  return session.Tournament.all()
    .toModelArray()
    .map(joins => {
      return {
        ...joins._fields,
      }
    })
})

export const getModeledMails = createSelector(orm, session => {
  return session.Mail.all()
    .toModelArray()
})

export const getModeledNotifications = createSelector(orm, session => {
  return session.Notification.all()
    .toModelArray()
})

export const getModeledContracts = createSelector(orm, session => {
  return session.Contract.all()
    .toModelArray()
});

export const getModeledRumbles = createSelector(orm, session => {
  return session.Rumble.all()
    .toModelArray()
});

export const getModeledLedgers = createSelector(orm, session => {
  return session.Ledger.all()
    .toModelArray()
});

export const getModeledInjuries = createSelector(orm, session => {
  return session.Injury.all()
    .toModelArray()
});

export const getModeledArenas = createSelector(orm, session => {
  return session.Arena.all()
    .toModelArray()
    .map(joins => {
      return {
        ...joins._fields,
        // show: joins?.show?._fields
      }
    })
})

export const getModeledAppearances = createSelector(orm, session => {
  return session.Appearance.all()
    .toModelArray()
    .map(joins => {
      return {
        ...joins._fields,
        wrestler: joins?.wrestler?._fields
        // segment: joins?.segment?._fields
      }
    })
})

export const getModeledBrands = createSelector(orm, session => {
  return session.Brand.all()
    .toModelArray()
    .map(joins => {
      return {
        ...joins._fields,
        director: joins?.director?._fields
      }
    })
})

export const getModeledChampionships = createSelector(orm, session => {
  return session.Championship.all()
    .toModelArray()
    .map(joins => {
      return {
        ...joins._fields,
        wrestlers: joins?.wrestlers?.toRefArray(),
        brands: joins?.brands?.toRefArray()
      }
    })
})

export const getModeledFeuds = createSelector(orm, session => {
  return session.Feud.all()
    .toModelArray()
    .map(joins => {
      return {
        ...joins._fields,
        championships: joins?.championships?.toRefArray(),
        winners: joins?.winners?.toRefArray(),
        wrestlers: joins?.wrestlers?.toRefArray(),
        brands: joins?.brands?.toRefArray()
      }
    })
})

export const getModeledProductions = createSelector(orm, session => {
  return session.Production.all()
    .toModelArray()
    .map(production => {
      const segments = session.Segment.filter(segment =>
        production.segmentIds.includes(segment.id)
      )
        .all()
        .toModelArray()
        .map((segment, index) => {
          const newSegment = modelSegment(segment, session)
          newSegment.order = index

          return newSegment;
        })

      return {
        ...production._fields,
        brands: production?.brands?.toRefArray() || [],
        show: production?.show?._fields,
        segments
      }
    })
})

export const getModeledFactions = createSelector(orm, session => {
  return session.Faction.all()
    .toModelArray()
    .map(joins => {
      return {
        ...joins._fields,
        leaders: joins?.leaders?.toRefArray(),
        managers: joins?.managers?.toRefArray(),
        brands: joins?.brands?.toRefArray(),
        wrestlers: joins?.wrestlers?.toRefArray()
      }
    })
})

export const getModeledSaves = createSelector(orm, session => {
  const {
    Appearance,
    Arena,
    Brand,
    Championship,
    Draft,
    Faction,
    Feud,
    Game,
    Production,
    Reign,
    Segment,
    Show,
    Wrestler,
  } = session
  const toPure = item => item?.all()?.toRefArray() || []
  const output = [
    {
      type: 'GENERATE_ARENAS',
      payload: toPure(Arena)
    },
    {
      type: 'GENERATE_APPEARANCES',
      payload: toPure(Appearance)
    },
    {
      type: 'GENERATE_DRAFTS',
      payload: toPure(Draft)
    },
    {
      type: 'GENERATE_BRANDS',
      payload: toPure(Brand)
    },
    {
      type: 'GENERATE_CHAMPIONSHIPS',
      payload: toPure(Championship),
    },
    {
      type: 'GENERATE_FACTIONS',
      payload: toPure(Faction),
    },
    {
      type: 'GENERATE_GAMES',
      payload: toPure(Game),
    },
    {
      type: 'GENERATE_REIGNS',
      payload: toPure(Reign),
    },
    {
      type: 'GENERATE_FEUDS',
      payload: toPure(Feud),
    },
    {
      type: 'GENERATE_SHOWS',
      payload: toPure(Show),
    },
    {
      type: 'GENERATE_SEGMENTS',
      payload: toPure(Segment),
    },
    {
      type: 'GENERATE_WRESTLERS',
      payload: toPure(Wrestler),
    },
    {
      type: 'GENERATE_PRODUCTIONS',
      payload: toPure(Production),
    }
  ]

  return output
})

export const getModeledSegments = createSelector(orm, session => {
  return session.Segment.all()
    .toModelArray()
    .map(segment => modelSegment(segment, session))
})

export const getModeledShows = createSelector(orm, session => {
  return session.Show.all()
    .toModelArray()
    .map(joins => {
      const values = {
        ...joins._fields,
        complete: joins._fields?.complete || false,
        brands: joins?.brands?.toRefArray(),
        referees: joins?.referees?.toRefArray(),
        interviewers: joins?.interviewers?.toRefArray(),
        commentators: joins?.commentators?.toRefArray(),
        authority: joins?.authority?.toRefArray()
      }
      return values
    })
})

export const getModeledDrafts = createSelector(orm, session => {
  return session.Draft.all()
    .toModelArray()
    .map(joins => {
      return {
        ...joins._fields,
        brand: joins?.brand?._fields,
        wrestler: joins?.wrestler?._fields
      }
    })
})

export const getModeledWrestlers = createSelector(orm, session => {
  return session.Wrestler.all()
    .toModelArray()
    .map(joins => {
      return {
        ...joins._fields,
        brands: joins?.brands?.toRefArray()
      }
    })
})

export const getModeledReigns = createSelector(orm, session => {
  return session.Reign.all()
    .toModelArray()
    .map(joins => {
      return {
        ...joins._fields,
        wrestlers: joins?.wrestlers?.toRefArray(),
        championship: joins?.championship?._fields
      }
    })
})

function sortArrOfObjByArrayOfIds(arr, ids) {
  return arr.sort((a, b) => {
    return ids.indexOf(a.id) - ids.indexOf(b.id)
  })
}

const modelAppearance = (appearance, session) => {
  const wrestler = session.Wrestler?.filter(
    wrestler => wrestler.id === appearance.wrestlerId
  ).toModelArray()?.[0]?._fields || {
      id: 'vacant', name: 'Vacant', image: '', points: 0, brandIds: []
  }

  return {
    ...appearance._fields,
    wrestler
  }
}

const modelSegment = (segment, session) => {
  const appearances = session.Appearance.filter(
    appearance => appearance.segmentId === segment._fields.id
  )
    .toModelArray()
    .map(appearance => modelAppearance(appearance, session))

  const winners = appearances?.filter(appearance => appearance.winner)
  const championships = segment?.championships?.toRefArray()
  const brands = segment?.brands?.toRefArray()
  const rawFeud = session.Feud?.filter(
    item => item.id === segment?._fields?.feudId
  ).toRefArray()[0]

  // FIX THIS NOT COMING THROUGH
  const feud = rawFeud ? modelFeud(rawFeud, session) : null

  return {
    ...segment._fields,
    championships,
    winners,
    feud,
    appearances,
    brands
  }
}

// toRefArray instance of feud, no _fields
const modelFeud = (feud, session) => {
  const rawBrands = session.Brand.all().toRefArray()
  const rawWrestlers = session.Wrestler.all().toRefArray()
  const rawChampionships = session.Championship.all().toRefArray()
  const brands = rawBrands.filter(item => feud?.brandIds?.includes(item.id))
  const championships = rawChampionships.filter(item => feud?.championshipIds?.includes(item.id))
  const wrestlers = rawWrestlers.filter(item => feud?.wrestlerIds?.includes(item.id))

  return {
    ...feud,
    brands,
    championships,
    wrestlers,
  }
}

export const getProductionsThisWeek = createSelector(orm, session => {
  const game = getSingleGame(session)
  const date = getDate(game)

  if (!date) {
    return []
  }

  return getProductionsForDate(date, session)
    .map(production => modelProduction(production, session))
})

export const modelProduction = (production, session) => {
  const segments = getSegmentsForProduction(production, session)
  return {
    ...production._fields,
    brands: production?.brands?.toRefArray() || [],
    show: production?.show?._fields,
    segmentIds: production.segmentIds,
    segments
  }
}

function getSingleGame(session) {
  return session.Game.all().toModelArray()?.[0]
}

function getDate(game) {
  return game?.date || new Date()
}

function getProductionsForDate(date, session) {
  return session.Production.filter(item => {
    return isSameWeek(new Date(item.date), new Date(date), { weekStartsOn: 1 })
  })
    .all()
    .toModelArray()
}

function getSegmentsForProduction(production, session) {
  const segmentIds = [...new Set(production.segmentIds.filter(item => item))]
  const rawSegments = session.Segment?.filter(
    segment => segmentIds.includes(segment.id)
  ).toModelArray()
  const mappedSegments = rawSegments.map(item => modelSegment(item, session))
  return sortArrOfObjByArrayOfIds(mappedSegments, segmentIds)
}


// stat selector
export const getEntityTotalsTotals = state => {
  return Object.keys(state.entitiesv2).reduce((o, val) => {
    o[val] = state?.entitiesv2?.[val]?.items?.length || 0
    return o
  }, {})
}
